import React, { useEffect } from 'react';
import { BookOpen, Link2, ListTree } from 'lucide-react';
import DashboardNav, { DashboardBrandRow } from './DashboardNav';
import ApiCatalogDashboard from './ApiCatalogDashboard';
import { mcpApiUrl } from './mcpApi';

function readPathname() {
  return typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') || '/' : '/';
}

function subLinkClass(active) {
  if (active) {
    return 'text-xs text-cyan-300 font-medium border-b border-cyan-500 pb-0.5';
  }
  return 'text-xs text-slate-500 hover:text-slate-300 border-b border-transparent pb-0.5';
}

function Code({ children }) {
  return (
    <code className="block text-[11px] text-emerald-200/90 bg-slate-950 p-2 rounded border border-slate-800 break-all whitespace-pre-wrap">
      {children}
    </code>
  );
}

function ApiConnectGuide() {
  const base = mcpApiUrl('/health').replace(/\/health\/?$/, '') || '(set REACT_APP_MCP_API_BASE or run npm start with proxy)';
  const catalog = mcpApiUrl('/v1/tools/catalog?include_details=false&hierarchy=false');

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-300">Connect to the HTTP API</h2>
        <p className="text-xs text-slate-400 mt-2 max-w-3xl">
          Use these steps to integrate apps, scripts, or gateways with the MCP tools HTTP server. For MCP stdio clients (Cursor, Claude
          Desktop), see <a href="/mcp-access" className="text-cyan-400 hover:underline">MCP Access</a>.
        </p>
      </div>

      <ol className="space-y-4 list-decimal list-inside text-xs text-slate-300">
        <li className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
          <span className="font-semibold text-slate-200">Base URL</span>
          <p className="mt-2 text-slate-400">Point clients at the catalog server (default local port 8082).</p>
          <Code>{base}</Code>
        </li>
        <li className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
          <span className="font-semibold text-slate-200">Authentication</span>
          <p className="mt-2 text-slate-400">
            If <code className="text-cyan-200">MCP_HTTP_API_KEY</code> is set on the server, send header{' '}
            <code className="text-cyan-200">X-API-KEY</code>. Match it in the dashboard via{' '}
            <code className="text-cyan-200">REACT_APP_MCP_HTTP_API_KEY</code>.
          </p>
        </li>
        <li className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
          <span className="font-semibold text-slate-200">Smoke test</span>
          <p className="mt-2 text-slate-400">Health and catalog:</p>
          <Code>{`${base}/health\nGET ${catalog}`}</Code>
        </li>
        <li className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
          <span className="font-semibold text-slate-200">App tokens</span>
          <p className="mt-2 text-slate-400">
            Create app tokens and grants under{' '}
            <a href="/account-hub/apps" className="text-emerald-400 hover:underline">
              App integrations
            </a>
            .
          </p>
        </li>
      </ol>
    </div>
  );
}

function HttpEndpointsReference() {
  const rows = [
    { method: 'GET', path: '/health', body: '—', desc: 'Liveness; JSON { status: ok }.' },
    {
      method: 'GET',
      path: '/v1/tools/catalog',
      body: 'Query: include_details, hierarchy, q',
      desc: 'MCP tool catalog (flat or hierarchical).',
    },
    { method: 'GET', path: '/v1/tools/describe', body: 'Query: tool_name (required)', desc: 'Single tool metadata.' },
    { method: 'GET', path: '/v1/account-hub/accounts', body: 'Query: user_id (optional)', desc: 'List linked accounts.' },
    { method: 'POST', path: '/v1/account-hub/accounts', body: 'JSON: account_id, provider, account_type, …', desc: 'Upsert connected account.' },
    { method: 'GET', path: '/v1/account-hub/apps', body: 'Query: user_id (optional)', desc: 'List external apps.' },
    { method: 'POST', path: '/v1/account-hub/apps', body: 'JSON: name, kind', desc: 'Register app; returns app_token.' },
    { method: 'POST', path: '/v1/account-hub/grants', body: 'JSON: app_id, scopes, transactional_access', desc: 'Update scopes / transactional flag.' },
    { method: 'POST', path: '/v1/account-hub/connectors/credentials', body: 'JSON: provider, key_alias, payload', desc: 'Store connector secret payload.' },
    { method: 'POST', path: '/v1/account-hub/mcp-edge/token', body: 'JSON: app_id', desc: 'Mint short-lived Bearer for MCP edge (Worker); set MCP_EDGE_BEARER_SECRET on engine and/or MCP server (same host as app registration).' },
    { method: 'GET', path: '/v1/account-hub/audit', body: 'Query: user_id, limit', desc: 'Recent audit events.' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 max-w-3xl">
        These routes are served by the MCP tools HTTP server (same process as the tool catalog). Your execution gateway may expose
        additional REST or WebSocket APIs on another port; consult your deployment config.
      </p>
      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 bg-slate-800/80 px-3 py-2 text-[10px] font-bold uppercase text-slate-400">
          <div className="col-span-2">Method</div>
          <div className="col-span-4">Path</div>
          <div className="col-span-3">Body / query</div>
          <div className="col-span-3">Notes</div>
        </div>
        <div className="divide-y divide-slate-800 bg-slate-900/50">
          {rows.map((r) => (
            <div key={`${r.method}-${r.path}`} className="grid grid-cols-12 gap-2 px-3 py-3 text-[11px]">
              <div className="col-span-2">
                <span
                  className={
                    r.method === 'GET'
                      ? 'text-cyan-300 font-semibold'
                      : r.method === 'POST'
                        ? 'text-amber-300 font-semibold'
                        : 'text-slate-300'
                  }
                >
                  {r.method}
                </span>
              </div>
              <div className="col-span-4 font-mono text-slate-200 break-all">{r.path}</div>
              <div className="col-span-3 text-slate-400">{r.body}</div>
              <div className="col-span-3 text-slate-400">{r.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ApiPortalDashboard() {
  const path = readPathname();

  useEffect(() => {
    if (path === '/api') {
      window.location.replace('/api/connect');
      return;
    }
    if (path.startsWith('/build-api-catalog')) {
      window.location.replace('/api/tools');
    }
  }, [path]);

  const isConnect = path === '/api/connect' || path === '/api';
  const isEndpoints = path.includes('/api/endpoints');
  const isTools =
    path.includes('/api/tools') || path.startsWith('/build-api-catalog');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 md:p-8 font-mono">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-slate-800 pb-4">
          <DashboardBrandRow />
          <DashboardNav />
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">API and integrations</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex items-center gap-1.5 text-slate-400 text-xs">
              <BookOpen size={14} className="text-cyan-500 shrink-0" />
              Sections
            </span>
            <a href="/api/connect" className={subLinkClass(isConnect)}>
              <span className="inline-flex items-center gap-1">
                <Link2 size={12} /> Connect
              </span>
            </a>
            <span className="text-slate-600">·</span>
            <a href="/api/endpoints" className={subLinkClass(isEndpoints)}>
              HTTP endpoints
            </a>
            <span className="text-slate-600">·</span>
            <a href="/api/tools" className={subLinkClass(isTools)}>
              <span className="inline-flex items-center gap-1">
                <ListTree size={12} /> MCP tools
              </span>
            </a>
          </div>
        </div>

        {isConnect ? <ApiConnectGuide /> : null}
        {isEndpoints ? <HttpEndpointsReference /> : null}
        {isTools ? <ApiCatalogDashboard embedded /> : null}
      </div>
    </div>
  );
}
